import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/http';
import { t, type MessageKey } from '../../i18n';

interface Item {
  id: string;
  title: string;
  subtitle: string;
  path: string;
}
interface Result {
  groups: { type: string; items: Item[] }[];
}

/** The search box in the page header: results appear as the user types and link to the page to open. */
export function GlobalSearch() {
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);

  // Wait for a pause in typing before asking the server.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);

  const found = useQuery({
    queryKey: ['search', term],
    queryFn: ({ signal }) => api<Result>('/search', { query: { q: term }, signal }),
    enabled: term.length >= 2,
  });
  const showing = open && term.length >= 2 && text.trim().length >= 2;

  return (
    <div className="relative hidden min-w-0 flex-1 sm:block sm:max-w-sm">
      <label htmlFor="global-search" className="sr-only">
        {t('search.label')}
      </label>
      <input
        id="global-search"
        type="search"
        autoComplete="off"
        maxLength={100}
        value={text}
        placeholder={t('search.placeholder')}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm"
      />
      {showing && (
        <div
          role="region"
          aria-label={t('search.label')}
          className="absolute z-20 mt-1 max-h-96 w-full overflow-y-auto rounded border border-slate-200 bg-white p-2 text-sm shadow-lg"
        >
          {found.isPending && <p role="status">{t('common.loading')}</p>}
          {found.data && found.data.groups.length === 0 && (
            <p className="p-2 text-slate-600">{t('search.none')}</p>
          )}
          {found.data?.groups.map((group) => (
            <div key={group.type} className="mb-2">
              <h2 className="px-2 text-xs font-semibold uppercase text-slate-500">
                {t(`search.type.${group.type}` as MessageKey)}
              </h2>
              <ul>
                {group.items.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={item.path}
                      onClick={() => {
                        setOpen(false);
                        setText('');
                      }}
                      className="block rounded px-2 py-1.5 hover:bg-slate-50"
                    >
                      <span className="font-medium">{item.title}</span>
                      {item.subtitle && (
                        <span className="block text-xs text-slate-600">{item.subtitle}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
